import { apiClient } from "@/api/api-client.ts";
import createBaseService from "@/api/base-service.ts";
import type { CaseActivity } from "../../types/activities";

const caseActivityService = {
	...createBaseService<CaseActivity>("/case-activities"),

	lisyByCaseId: async (id, params = {}) => {
		const response = await apiClient.get<{ data: T[]; meta: Pagination }>(`/case/${id}/activities`, { params });

		return response.data;
	},

	lisyByCaseComplianceId: async (id, params = {}) => {
		const response = await apiClient.get<{ data: T[]; meta: Pagination }>(`/case/${id}/compliances`, { params });

		return response.data;
	},

	lisyByCaseNotificationId: async (id, params = {}) => {
		const response = await apiClient.get<{ data: T[]; meta: Pagination }>(`/case/${id}/notifications`, { params });

		return response.data;
	},

	lisyByCaseDeletionId: async (id, params = {}) => {
		const response = await apiClient.get<{ data: T[]; meta: Pagination }>(`/case/${id}/deletions`, { params });

		return response.data;
	},

	ammendAdminByIdWithMotion: async (data: T) => {
		const response = await apiClient.post<{ data: T }>("/admin-case-ammendments", data, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});
		return response.data.data;
	},

	ammendByIdWithMotion: async (data: T) => {
		const response = await apiClient.post<{ data: T }>("/case-ammendments", data, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});
		return response.data.data;
	},

	getStenographicNotes: async (params = {}) => {
		const response = await apiClient.get<{ data: T[]; meta: Pagination }>("/stenographic-notes", { params });

		return response.data;
	},

	getSheriffReports: async (params = {}) => {
		const response = await apiClient.get<{ data: T[]; meta: Pagination }>("/sheriff-reports", { params });

		return response.data;
	},

	grantViewRequest: async (data: any) => {
		const response = await apiClient.post<{ data: CaseActivity }>(`/case-activities/${data}/grant-view-request`, data);
		return response.data.data;
	},

	createById: async (data: any) => {
		const response = await apiClient.post<{ data: CaseActivity }>(`/case/${data.case_id}/activities`, data, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});
		return response.data.data;
	},

	scheduleHearing: async (data: any) => {
		const response = await apiClient.post<{ data: CaseActivity }>(`/case/${data.case_id}/hearing`, data, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});
		return response.data.data;
	},

	createByHearing: async (data: T) => {
		const response = await apiClient.post<{ data: T }>(`/case-activities/${data.id}/hearing`, data, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});
		return response.data.data;
	},

	shareById: async (data: T) => {
		const response = await apiClient.post<{ data: T }>(`/case-activities/${data.id}/share-activity`, data);
		return response.data.data;
	},

	updateStage: async (data: T) => {
		const response = await apiClient.post<{ data: T }>(`/case-activities/${data.id}/move-stages`, data);
		return response.data.data;
	},

	updateCaseStage: async (data: T) => {
		const response = await apiClient.post<{ data: T }>(`/case-activities/${data.id}/update-stage`, data);
		return response.data.data;
	},

	updateById: async (data: T) => {
		const response = await apiClient.post<{ data: T }>(`/backoffice/case/${data.case_id}/activities/mom`, data, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});
		return response.data.data;
	},

	updateActivityById: async (data: T) => {
		const response = await apiClient.post<{ data: T }>(`/backoffice/case/${data.case_id}/activities/${data.id}`, data, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});
		return response.data.data;
	},

	updateSubActivityById: async (data: T) => {
		const response = await apiClient.post<{ data: T }>(`/backoffice/case/${data.case_id}/activities/sub-activity`, data, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});
		return response.data.data;
	},
	updateProofById: async (data: T) => {
		const response = await apiClient.post<{ data: T }>(`/backoffice/case/${data.case_id}/activities/proof`, data, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});
		return response.data.data;
	},

	convertDocstoPdf: async (data: T) => {
		const response = await apiClient.post<{ data: T }>(
			"/convert-docs-to-pdf",
			{
				file: data,
			},
			{
				headers: {
					"Content-Type": "multipart/form-data",
				},
			},
		);
		return response.data;
	},

	updateTitleById: async (data: T) => {
		const response = await apiClient.post<{ data: T }>(`/case-activities/${data.id}/update-title`, data, {});
		return response.data.data;
	},

	deleteWithReason: async (data: T) => {
		const response = await apiClient.post<{ data: T }>(`/case-activities/${data.id}/delete-with-reason`, data, {});
		return response.data.data;
	},

	notifyById: async (data: T) => {
		const response = await apiClient.post<{ data: T }>(`/case-activities/${data.id}/notify`, data, {});
		return response.data.data;
	},

	updateStatusById: async (data: T) => {
		const response = await apiClient.post<{ data: T }>(`/case-activities/${data.id}/update-status`, data, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});
		return response.data.data;
	},
	approvalById: async (data: T) => {
		const response = await apiClient.post<{ data: T }>(`/case-activities/${data.id}/approval`, data);
		return response.data.data;
	},

	findByCaseId: async (caseId, id: string) => {
		const response = await apiClient.get<{ data: T }>(`/case/${caseId}/activities/${id}`);

		return response.data.data;
	},
	createByIdWithMotion: async (data: T) => {
		const response = await apiClient.post<{ data: T }>(`/case/${data.case_id}/activities-complainant`, data, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});
		return response.data.data;
	},
	updateSheriffReport: async (data: T) => {
		const response = await apiClient.post<{ data: T }>(`/case/${data.case_id}/activities-sheriff`, data, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});
		return response.data.data;
	},
	updateSheriffReportFile: async (data: T) => {
		const response = await apiClient.post<{ data: T }>(`/case/${data.case_id}/activities-sheriff-file`, data, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});
		return response.data.data;
	},
};

export default caseActivityService;
